import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import StarRating from '../components/StarRating';
import ServiceCard from '../components/ServiceCard';
import ReportModal from '../components/ReportModal';
import useFavoritesStore from '../store/useFavoritesStore';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';

function SimilarServices({ category, excludeId }) {
  const [similar, setSimilar] = useState([]);
  useEffect(() => {
    if (!category) return;
    api.get(`/services?category=${category}&limit=6`)
      .then(r => setSimilar((r.data.services || []).filter(s => s.id !== excludeId).slice(0, 6)))
      .catch(() => {});
  }, [category, excludeId]);
  if (similar.length === 0) return null;
  return (
    <div className="mb-6">
      <h2 className="text-base font-bold text-dark-text mb-3 px-4">Similar Services</h2>
      <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
        {similar.map(s => (
          <div key={s.id} className="flex-shrink-0 w-44">
            <ServiceCard service={s} />
          </div>
        ))}
      </div>
    </div>
  );
}

const CATEGORY_LABELS = {
  MODELS: 'Models',
  VOICEOVER: 'Voiceover',
  DESIGN_BRANDING: 'Design & Branding',
  MARKETING_SOCIAL: 'Marketing & Social',
  VIDEO_PHOTOGRAPHY: 'Video & Photography',
  WEB_DEVELOPMENT: 'Web & Development',
  CONTENT_CREATION: 'Content Creation',
  STUDIO_RENTALS: 'Studio Rentals',
  EVENT_PLANNING: 'Event Planning'
};

export default function ServiceListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currency } = useCurrencyStore();
  const { toggle, isFavorite } = useFavoritesStore();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({ startDate: '', endDate: '', notes: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);

  // Review modal state
  const [showReview, setShowReview] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [showReport, setShowReport] = useState(false);
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');

  const fetchService = () => {
    api.get(`/services/${id}`)
      .then(r => { setService(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchService();
  }, [id]);

  useEffect(() => {
    if (searchParams.get('review') === '1') setShowReview(true);
  }, []);

  const handleBook = async () => {
    if (!booking.startDate) return;
    setBookingLoading(true);
    try {
      await api.post('/bookings', { serviceId: id, ...booking });
      setBookingSuccess(true);
      setTimeout(() => navigate('/bookings'), 1500);
    } catch (err) {
      useToastStore.getState().addToast(err.response?.data?.error || 'Booking failed', 'error');
    }
    setBookingLoading(false);
  };

  const handleSubmitReview = async () => {
    try {
      await api.post('/reviews', {
        serviceId: id,
        bookingId,
        rating: review.rating,
        comment: review.comment
      });
      setShowReview(false);
      setLoading(true);
      fetchService();
    } catch (err) {
      useToastStore.getState().addToast(err.response?.data?.error || 'Failed to submit review', 'error');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!service) return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center text-dark-subtext">
      Service not found
    </div>
  );

  const isStudio = service.listingType === 'STUDIO';
  const favorited = isFavorite(service.id);
  const totalImages = service.images?.length || 0;
  const isVideo = (src) => /\.(mp4|mov|avi|mkv|webm)$/i.test(src) || src?.includes('/video/');
  const avgRating = service.reviews?.length > 0
    ? (service.reviews.reduce((sum, r) => sum + r.rating, 0) / service.reviews.length)
    : (service.provider?.rating || 0);

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* ── Header buttons (over gallery) ── */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-12 left-4 z-20 bg-dark-bg/60 backdrop-blur-md p-2.5 rounded-full"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <div className="fixed top-12 right-4 z-20 flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); toggle(service); }}
          className="bg-dark-bg/60 backdrop-blur-md p-2.5 rounded-full"
          aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          {favorited ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-red-500">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          )}
        </button>
        <button
          onClick={() => {
            const url = window.location.href;
            if (navigator.share) {
              navigator.share({ title: service?.title, url }).catch(() => {});
            } else {
              navigator.clipboard.writeText(url);
              useToastStore.getState().addToast('Link copied!', 'success');
            }
          }}
          className="bg-dark-bg/60 backdrop-blur-md p-2.5 rounded-full"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="2">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </button>
      </div>

      {/* ── Gallery ── */}
      <div
        className="h-72 bg-dark-surface relative overflow-hidden"
        onTouchStart={e => setTouchStart(e.touches[0].clientX)}
        onTouchEnd={e => {
          if (touchStart === null) return;
          const diff = touchStart - e.changedTouches[0].clientX;
          if (diff > 50 && activeImg < totalImages - 1) setActiveImg(i => i + 1);
          if (diff < -50 && activeImg > 0) setActiveImg(i => i - 1);
          setTouchStart(null);
        }}
      >
        {totalImages > 0 ? (
          isVideo(service.images[activeImg]) ? (
            <video
              src={service.images[activeImg]}
              className="w-full h-full object-cover"
              controls
              playsInline
              muted
            />
          ) : (
            <img src={service.images[activeImg]} alt="" className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full bg-brand-gradient opacity-30 flex items-center justify-center">
            <span className="text-6xl">{isStudio ? '🎙️' : '🎨'}</span>
          </div>
        )}
        {/* Image counter badge */}
        {totalImages > 1 && (
          <span className="absolute bottom-3 right-3 bg-dark-bg/70 backdrop-blur text-white text-xs font-medium px-2.5 py-1 rounded-full">
            {activeImg + 1} of {totalImages}
          </span>
        )}
        {/* Dot indicators */}
        {totalImages > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 items-center pointer-events-none">
            {service.images.map((_, i) => (
              <span
                key={i}
                className={`rounded-full transition-all ${i === activeImg ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Provider row ── */}
      <div className="px-4 pt-4 pb-3">
        <button
          onClick={() => navigate(`/provider/${service.provider?.id}`)}
          className="flex items-center gap-3 w-full"
        >
          <div className="w-10 h-10 rounded-full bg-brand-gradient flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
            {service.provider?.avatar
              ? <img src={service.provider.avatar} className="w-full h-full object-cover" alt="" />
              : service.provider?.fullName?.[0]}
          </div>
          <div className="min-w-0 text-left">
            <p className="text-dark-text text-sm font-semibold truncate">
              {service.provider?.companyName || service.provider?.fullName}
            </p>
            <div className="flex items-center gap-1.5">
              <StarRating rating={service.provider?.rating || 0} size={12} />
              <span className="text-dark-subtext text-xs">({service.provider?.reviewCount || 0})</span>
            </div>
          </div>
          {user?.id !== service.provider?.id && (
            <span
              onClick={async (e) => {
                e.stopPropagation();
                try { await api.post(`/messages/direct/${service.provider?.id}`); } catch {}
                navigate('/inbox');
              }}
              className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl bg-dark-surface border border-dark-border text-dark-text text-xs font-medium"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              Message
            </span>
          )}
        </button>
      </div>

      {/* ── Divider ── */}
      <div className="h-px bg-dark-border mx-4" />

      {/* ── Title ── */}
      <div className="px-4 pt-4">
        <h1 className="text-xl font-bold text-dark-text leading-snug" style={{ letterSpacing: '-0.3px' }}>
          {service.title}
        </h1>
      </div>

      {/* ── Description ── */}
      <div className="px-4 pt-3 pb-4">
        <p className={`text-dark-subtext text-sm leading-relaxed ${!descExpanded ? 'line-clamp-3' : ''}`}>
          {service.description}
        </p>
        {service.description?.length > 150 && (
          <button
            onClick={() => setDescExpanded(v => !v)}
            className="text-brand-purple text-sm font-medium mt-1"
          >
            {descExpanded ? 'Less' : '...More'}
          </button>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="h-2 bg-dark-bg" />

      {/* ── Price & Details Card ── */}
      <div className="px-4 py-4">
        <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl font-bold text-brand-purple">
              {isStudio ? `${formatPrice(service.hourlyRate || service.price, currency)}/hr` : formatPrice(service.price, currency)}
            </span>
            <span className="text-xs text-dark-subtext bg-brand-purple/10 text-brand-purple px-2.5 py-1 rounded-full font-medium">
              {CATEGORY_LABELS[service.category] || service.category}
            </span>
          </div>

          {service.deliveryDays && (
            <div className="flex items-center gap-2 py-2.5 border-t border-dark-border">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-dark-subtext">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <span className="text-dark-text text-sm">Delivery</span>
              <span className="text-dark-text text-sm font-semibold ml-auto">{service.deliveryDays} Days</span>
            </div>
          )}

          {/* Tags as features */}
          {service.tags?.length > 0 && (
            <div className="pt-2.5 border-t border-dark-border">
              <div className="flex flex-wrap gap-2">
                {service.tags.map(t => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-dark-bg text-dark-subtext border border-dark-border">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Studio specifics ── */}
      {isStudio && (
        <div className="px-4 pb-4">
          {service.equipment?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-bold text-dark-text mb-2">Equipment</h3>
              <div className="flex flex-wrap gap-2">
                {service.equipment.map(e => (
                  <span key={e} className="text-xs px-3 py-1.5 rounded-full bg-dark-surface border border-dark-border text-dark-subtext flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
                    {e}
                  </span>
                ))}
              </div>
            </div>
          )}
          {service.amenities?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-bold text-dark-text mb-2">Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {service.amenities.map(a => (
                  <span key={a} className="text-xs px-3 py-1.5 rounded-full bg-dark-surface border border-dark-border text-dark-subtext flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Availability ── */}
      {service.availableDays?.length > 0 && (
        <div className="px-4 pb-4">
          <h2 className="text-base font-bold text-dark-text mb-2">Availability</h2>
          <div className="bg-dark-surface border border-dark-border rounded-2xl p-3">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {['MON','TUE','WED','THU','FRI','SAT','SUN'].map(d => (
                <span key={d} className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  service.availableDays.includes(d)
                    ? 'bg-brand-purple/20 text-brand-purple border border-brand-purple/30'
                    : 'bg-dark-bg text-dark-subtext/30 border border-dark-border'
                }`}>
                  {d.charAt(0) + d.slice(1).toLowerCase()}
                </span>
              ))}
            </div>
            {service.availableFrom && service.availableTo && (
              <p className="text-dark-subtext text-xs">{service.availableFrom} – {service.availableTo}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Divider ── */}
      <div className="h-2 bg-dark-bg" />

      {/* ── Booking section ── */}
      {user?.id !== service.provider?.id && (
        <div className="px-4 py-4">
          <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
            <h2 className="text-base font-bold text-dark-text mb-3">
              {isStudio ? 'Book Studio' : 'Request Service'}
            </h2>
            <div className="flex flex-col gap-3">
              {isStudio && (
                <>
                  <div>
                    <label className="text-xs text-dark-subtext mb-1 block">Start Date & Time</label>
                    <input
                      type="datetime-local"
                      value={booking.startDate}
                      onChange={e => setBooking(b => ({ ...b, startDate: e.target.value }))}
                      className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2.5 text-dark-text text-sm outline-none focus:border-brand-purple"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-dark-subtext mb-1 block">End Date & Time</label>
                    <input
                      type="datetime-local"
                      value={booking.endDate}
                      onChange={e => setBooking(b => ({ ...b, endDate: e.target.value }))}
                      className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2.5 text-dark-text text-sm outline-none focus:border-brand-purple"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                </>
              )}
              <div>
                <label className="text-xs text-dark-subtext mb-1 block">Notes (optional)</label>
                <textarea
                  value={booking.notes}
                  onChange={e => setBooking(b => ({ ...b, notes: e.target.value }))}
                  placeholder="Describe your project or requirements..."
                  rows={3}
                  className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2.5 text-dark-text text-sm outline-none focus:border-brand-purple resize-none"
                />
              </div>
            </div>

            {bookingSuccess ? (
              <div className="mt-3 py-4 rounded-2xl bg-green-500/20 border border-green-500/30 text-green-400 text-center font-semibold">
                Booking sent! Redirecting...
              </div>
            ) : (
              <button
                onClick={handleBook}
                disabled={bookingLoading || (isStudio && !booking.startDate)}
                className="w-full mt-3 py-4 rounded-2xl bg-brand-gradient text-white font-semibold text-base glow disabled:opacity-60"
              >
                {bookingLoading ? 'Sending...' : isStudio ? 'Book Studio' : `Continue (${formatPrice(service.price, currency)})`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Divider ── */}
      {service.reviews?.length > 0 && <div className="h-2 bg-dark-bg" />}

      {/* ── Reviews ── */}
      {service.reviews?.length > 0 && (
        <div className="py-4">
          {/* Rating summary */}
          <div className="px-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className={`text-xl ${s <= Math.round(avgRating) ? 'text-yellow-400' : 'text-dark-muted'}`}>★</span>
                ))}
              </div>
              <span className="text-xl font-bold text-dark-text">{avgRating.toFixed(1)}</span>
            </div>
            <p className="text-dark-subtext text-sm">{service.reviews.length} review{service.reviews.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Horizontal scrollable review cards */}
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
            {service.reviews.map(r => (
              <div key={r.id} className="flex-shrink-0 w-72 bg-dark-surface border border-dark-border rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-brand-gradient flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {r.reviewer?.avatar
                      ? <img src={r.reviewer.avatar} className="w-full h-full rounded-full object-cover" alt="" />
                      : r.reviewer.fullName[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-dark-text text-sm font-medium truncate">{r.reviewer.fullName}</p>
                  </div>
                </div>
                {r.comment && <p className="text-dark-subtext text-xs leading-relaxed line-clamp-4 mb-2">{r.comment}</p>}
                <div className="flex items-center justify-between mt-auto">
                  <StarRating rating={r.rating} size={13} />
                  <span className="text-dark-subtext text-xs font-medium">{r.rating.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Divider ── */}
      <div className="h-2 bg-dark-bg" />

      {/* ── Similar services (horizontal scroll) ── */}
      <div className="pt-4">
        <SimilarServices category={service.category} excludeId={service.id} />
      </div>

      {/* ── Report link ── */}
      {user?.id !== service.provider?.id && (
        <div className="text-center py-2 pb-4">
          <button onClick={() => setShowReport(true)} className="text-dark-subtext text-xs underline underline-offset-2">
            Report this listing
          </button>
        </div>
      )}

      {showReport && (
        <ReportModal
          targetType="SERVICE"
          targetId={service.id}
          targetName={service.title}
          onClose={() => setShowReport(false)}
        />
      )}

      {/* ── Review modal ── */}
      {showReview && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-dark-surface border border-dark-border rounded-t-3xl w-full max-w-lg p-6 pb-10">
            <h2 className="text-lg font-bold text-dark-text mb-5 text-center">Leave a Review</h2>

            <div className="flex justify-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setReview(r => ({ ...r, rating: star }))}
                  className="text-3xl transition-transform active:scale-90"
                  aria-label={`${star} star`}
                >
                  <span className={star <= review.rating ? 'text-yellow-400' : 'text-dark-muted'}>★</span>
                </button>
              ))}
            </div>

            <textarea
              value={review.comment}
              onChange={e => setReview(r => ({ ...r, comment: e.target.value }))}
              placeholder="Share your experience..."
              rows={4}
              className="w-full bg-dark-bg border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple resize-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={handleSubmitReview}
                className="flex-1 py-3 rounded-xl bg-brand-gradient text-white font-semibold text-sm"
              >
                Submit Review
              </button>
              <button
                onClick={() => setShowReview(false)}
                className="flex-1 py-3 rounded-xl bg-dark-bg border border-dark-border text-dark-subtext font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
